import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { removeWord } from "../constants";
import {
  delete_product,
  update_quotation_sequence,
  update_quotation_single_product,
  create_quotation_single_product
} from "../services/order";
import QuotationProductAddModal from "./QuotationProductAddModal";
import QuotationProductEditModal from "./QuotationProductEditModal";
import { message, Popconfirm, Switch } from "antd";
import { FaPlus } from "react-icons/fa6";

const FONT_SIZE_VIEW = "15px";
const PADDING_TOP_TABLE = "3px";
const PADDING_BOTTOM_TABLE = "3px";

type OrderViewOneTableProps = {
  selecteduitable: string;
  filteredProducts: any[];
  roundedTotalAmount?: number;
  isDiscountShow: boolean;
  isGstShow: boolean;
  onChangeRows?: (rows: any[]) => void;
};

type SegmentItem = { data: any; index: number };
type Segment = { categoryName: string; items: SegmentItem[] };
type Row =
  | { type: "header"; categoryName: string }
  | { type: "item"; data: any; globalIndex: number };
type TableBlock = { rows: Row[] };

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

const computeDerived = (args: {
  price: number;
  discount: number;
  gst_percentage: number;
  quotationQty: number;
}) => {
  const price = Number(args.price) || 0;
  const discount = Number(args.discount) || 0;
  const gst_percentage = Number(args.gst_percentage) || 0;
  const quotationQty = Number(args.quotationQty) || 0;

  const discountValue = round2((price * discount) / 100);
  const discountAmount = round2(price - discountValue);
  const gstValue = round2((discountAmount * gst_percentage) / 100);
  const gstAmount = round2(discountAmount + gstValue);
  const totalPrice = round2(gstAmount * quotationQty);

  return {
    price,
    discount,
    gst_percentage,
    quotationQty,
    discountValue,
    discountAmount,
    gstValue,
    gstAmount,
    totalPrice,
  };
};

const OrderViewOnetable = ({
  selecteduitable,
  filteredProducts,
  isDiscountShow,
  isGstShow,
  onChangeRows,
}: OrderViewOneTableProps) => {
  // Keep local items sorted by srno so array indices match DB truth
  const [items, setItems] = useState<any[]>(() =>
    (filteredProducts || [])
      .slice()
      .sort((a, b) => (a?.attribute?.srno ?? 0) - (b?.attribute?.srno ?? 0))
  );

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addIndex, setAddIndex] = useState<number | null>(null);
  const [search, setSearch] = useState<string>("");
  const [isSplit, setIsSplit] = useState<boolean>(true);

  const searchLC = useMemo(() => (search || "").trim().toLowerCase(), [search]);

  useEffect(() => {
    const next = (filteredProducts || [])
      .slice()
      .sort((a, b) => (a?.attribute?.srno ?? 0) - (b?.attribute?.srno ?? 0));
    setItems(next);
  }, [filteredProducts]);

  const tablesWrapRef = useRef<HTMLDivElement | null>(null);

  const [edit, setEdit] = useState<null | {
    gIdx: number;
    field: "price" | "discount" | "gst_percentage" | "quotationQty";
    value: string;
  }>(null);

  const beginEdit = (
    gIdx: number,
    field: "price" | "discount" | "gst_percentage" | "quotationQty",
    currentValue: number | string
  ) => setEdit({ gIdx, field, value: String(currentValue ?? "") });

  const cancelEdit = () => setEdit(null);

  const commitEdit = async () => {
    if (!edit) return;
    const { gIdx, field, value } = edit;
    const row = items[gIdx];
    if (!row) return;

    const base = {
      price: Number(row?.attribute?.price) || 0,
      discount: Number(row?.attribute?.discount) || 0,
      gst_percentage: Number(row?.attribute?.gst_percentage) || 0,
      quotationQty: Number(row?.attribute?.quotationQty) || 0,
    };
    const nextBase = { ...base, [field]: Number(value) || 0 };
    const calc = computeDerived(nextBase);

    const payload = {
      qty: Number(row?.attribute?.qty) || 0,
      price: calc.price,
      discount: calc.discount,
      discountValue: calc.discountValue,
      discountAmount: calc.discountAmount,
      gstAmount: calc.gstAmount,
      totalPrice: calc.totalPrice,
      quotationQty: calc.quotationQty,
      gst_percentage: calc.gst_percentage,
      gstValue: calc.gstValue,
      updatedDttm: Date.now(),
      id: row?.attribute?.id,
      quotationId: row?.attribute?.quotationId,
      productId: row?.attribute?.productId,
      companyId: row?.attribute?.companyId,
      unitId: row?.attribute?.unitId,
    };

    try {
      await update_quotation_single_product(payload);
      const next = items.map((r, i) =>
        i === gIdx
          ? {
              ...r,
              attribute: {
                ...r.attribute,
                price: calc.price,
                discount: calc.discount,
                discountValue: calc.discountValue,
                discountAmount: calc.discountAmount,
                gst_percentage: calc.gst_percentage,
                gstValue: calc.gstValue,
                gstAmount: calc.gstAmount,
                quotationQty: calc.quotationQty,
                totalPrice: calc.totalPrice,
              },
            }
          : r
      );
      setItems(next);
      onChangeRows?.(next);
      setEdit(null);
    } catch (e: any) {
      console.error(e);
      message.error("Something went wrong please contact to devminds");
    }
  };

  const dragFromRef = useRef<number | null>(null);
  const seqPendingRef = useRef<boolean>(false);

  const reorderAndRenumber = (arr: any[], from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
    const next = arr.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next.map((row, i) => ({
      ...row,
      attribute: { ...row.attribute, srno: i + 1 },
    }));
  };

  // Central move: up/down/drag
  const performMove = async (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    if (seqPendingRef.current) {
      message.info("Please wait… finishing previous reorder.");
      return;
    }

    const prevItems = items;
    const fromRow = items[from];
    const toRow = items[to];
    if (!fromRow || !toRow) return;

    const nextItems = reorderAndRenumber(items, from, to);
    setItems(nextItems);
    onChangeRows?.(nextItems);

    const place = from < to ? "after" : "before";
    seqPendingRef.current = true;
    try {
      await update_quotation_sequence({
        from_id: fromRow.attribute.id,
        to_id: toRow.attribute.id,
        place,
      });
    } catch (err: any) {
      console.error(err);
      setItems(prevItems);
      onChangeRows?.(prevItems);
      message.error(err?.message || "Failed to update sequence. Reverted.");
    } finally {
      seqPendingRef.current = false;
    }
  };

  const borderColor =
    selecteduitable === "SOHAM FIRM" ? "rgb(166, 166, 192)" : "rgb(175 193 123 / 49%)";
  const bgColor =
    selecteduitable === "SOHAM FIRM" ? "rgb(166, 166, 192)" : "rgb(175 193 123 / 78%)";

  const styles: any = {
    tableWrap: { marginBottom: 12 },
    table: {
      width: "100%",
      borderCollapse: "collapse" as const,
      fontFamily: "Arial, sans-serif",
      border: `2px solid ${borderColor}`,
    },
    tableHead: { backgroundColor: bgColor, textAlign: "left" as const, color: "#2c2c2c" },
    tableCell: {
      padding: "6px",
      border: `1px solid ${borderColor}`,
      fontWeight: "bold",
      fontSize: FONT_SIZE_VIEW,
      textAlign: "center" as const,
      wordWrap: "break-word" as const,
      paddingTop: PADDING_TOP_TABLE,
      paddingBottom: PADDING_BOTTOM_TABLE,
    },
    tableCell2: {
      padding: "6px",
      border: `1px solid ${borderColor}`,
      fontSize: FONT_SIZE_VIEW,
      textAlign: "center" as const,
      paddingTop: PADDING_TOP_TABLE,
      paddingBottom: PADDING_BOTTOM_TABLE,
      cursor: "default",
    },
    tableCellEditable: {
      padding: "6px",
      border: `1px solid ${borderColor}`,
      fontSize: FONT_SIZE_VIEW,
      textAlign: "center" as const,
      paddingTop: PADDING_TOP_TABLE,
      paddingBottom: PADDING_BOTTOM_TABLE,
      cursor: "text",
      background: "rgba(255,255,0,0.06)",
    },
    input: {
      width: "100%",
      boxSizing: "border-box" as const,
      fontSize: FONT_SIZE_VIEW,
      padding: "2px 4px",
      textAlign: "right" as const,
    },
    tableCell3: {
      whiteSpace: "normal" as const,
      overflow: "hidden",
      textOverflow: "ellipsis",
      textAlign: "left" as const,
    },
    sectionHeaderCell: {
      padding: "6px",
      border: `1px solid ${borderColor}`,
      fontSize: "12px",
      fontWeight: 700 as const,
      textAlign: "left" as const,
      backgroundColor: "rgba(0,0,0,0.03)",
    },
    totalCell: {
      padding: "6px",
      textAlign: "center" as const,
      fontWeight: "bold",
      fontSize: FONT_SIZE_VIEW,
      borderTop: `2px solid ${borderColor}`,
      paddingTop: PADDING_TOP_TABLE,
      paddingBottom: PADDING_BOTTOM_TABLE,
    },
    totalAmount: {
      padding: "6px",
      fontWeight: "bold",
      textAlign: "center" as const,
      fontSize: FONT_SIZE_VIEW,
      borderLeft: `2px solid ${borderColor}`,
      borderTop: `2px solid ${borderColor}`,
      paddingTop: PADDING_TOP_TABLE,
      paddingBottom: PADDING_BOTTOM_TABLE,
    },
    srCellInner: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 },
    iconBtn: {
      border: "none",
      background: "transparent",
      cursor: "pointer",
      padding: 0,
      fontSize: 12,
      lineHeight: 1,
    },
    smallIcon: { fontSize: 12 },
    exportWrap: { display: "flex", justifyContent: "flex-end", marginBottom: 8 },
    exportBtn: {
      border: `1px solid ${borderColor}`,
      background: "#fff",
      padding: "6px 10px",
      borderRadius: 6,
      cursor: "pointer",
      fontSize: 12,
    },
    searchBarWrap: { display: "flex", justifyContent: "flex-end", marginBottom: 8 },
    searchInput: {
      border: `1px solid ${borderColor}`,
      background: "#fff",
      padding: "6px 10px",
      borderRadius: 6,
      fontSize: 12,
      minWidth: 260,
    },
  };

  const PrintHider = () => <style>{`@media print { .no-print { display:none !important } }`}</style>;

  const handleDelete = (globalIndex: number, row: any) => {
    const next = items.filter((_, i) => i !== globalIndex);
    setItems(next);
    onChangeRows?.(next);
    delete_product(
      row.data.attribute.quotationId,
      row.data.attribute.id,
      row.data.attribute.totalPrice
    );
  };

  const moveUp = (idx: number) => performMove(idx, idx - 1);
  const moveDown = (idx: number) => performMove(idx, idx + 1);

  const onDragStartHandle = (fromIndex: number, e: React.DragEvent) => {
    dragFromRef.current = fromIndex;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(fromIndex));
    const ghost = document.createElement("div");
    ghost.style.padding = "4px 8px";
    ghost.style.background = "#eee";
    ghost.style.border = "1px solid #ccc";
    ghost.style.fontSize = "12px";
    ghost.innerText = "Moving row…";
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const segments: Segment[] = useMemo(() => {
    const segs: Segment[] = [];
    let cur: Segment | null = null;

    const matches = (p: any, q: string) => {
      if (!q) return true;
      const name = String(p?.name || "").toLowerCase();
      const code = String(p?.attribute?.productCode || p?.code || "").toLowerCase();
      const company = String(p?.attribute?.companyName || p?.companyName || "").toLowerCase();
      return [name, code, company].some((s) => s.includes(q));
    };

    items.forEach((p, idx) => {
      if (searchLC && !matches(p, searchLC)) return;
      const cat = p?.attribute?.categoryName ?? "";
      if (!cur || cur.categoryName !== cat) {
        if (cur) segs.push(cur);
        cur = { categoryName: cat, items: [{ data: p, index: idx }] };
      } else {
        cur.items.push({ data: p, index: idx });
      }
    });

    if (cur) segs.push(cur);
    return segs;
  }, [items, searchLC]);

  const SPLIT_THRESHOLD = 3;

  type Seg = {
    categoryName: string;
    items: { data: any; index: number }[];
  };

  // const tables: TableBlock[] = useMemo(() => {
  //   const out: TableBlock[] = [];
  //   const filteredSegments = (segments || []).filter(
  //     (seg) => (seg.categoryName || "").toUpperCase() !== "OTHER"
  //   );
  //   if (!filteredSegments.length) return out;

  //   let i = 0;
  //   while (i < filteredSegments.length) {
  //     const first = filteredSegments[i] as Seg;
  //     const headerCategory = first.categoryName;

  //     const block: TableBlock = {
  //       rows: [{ type: "header", categoryName: headerCategory }],
  //     };
  //     first.items.forEach(({ data, index }) =>
  //       block.rows.push({ type: "item", data, globalIndex: index })
  //     );
  //     i++;

  //     while (i < filteredSegments.length) {
  //       const seg = filteredSegments[i] as Seg;
  //       const isBig = seg.items.length >= SPLIT_THRESHOLD;

  //       if (!isBig || seg.categoryName === headerCategory) {
  //         seg.items.forEach(({ data, index }) =>
  //           block.rows.push({ type: "item", data, globalIndex: index })
  //         );
  //         i++;
  //         continue;
  //       }
  //       break;
  //     }

  //     out.push(block);
  //   }

  //   return out;
  // }, [segments]);
const tables: TableBlock[] = useMemo(() => {
  const out: TableBlock[] = [];
  const filteredSegments = (segments || []).filter(
    (seg) => (seg.categoryName || "").toUpperCase() !== "OTHER"
  );
  if (!filteredSegments.length) return out;

  let i = 0;
  while (i < filteredSegments.length) {
    const first = filteredSegments[i] as Seg;
    const headerCategory = first.categoryName;

    const block: TableBlock = {
      rows: [{ type: "header", categoryName: headerCategory }],
    };

    // Add first segment rows
    first.items.forEach(({ data, index }) =>
      block.rows.push({ type: "item", data, globalIndex: index })
    );
    i++;

    // Try to extend this block with following segments
    while (i < filteredSegments.length) {
      const seg = filteredSegments[i] as Seg;
      const isBig = seg.items.length >= SPLIT_THRESHOLD;

      // 🔑 Only start a new table when isSplit === true
      const shouldStartNewBlock =
        isSplit && isBig && seg.categoryName !== headerCategory;

      if (shouldStartNewBlock) {
        // keep old behavior: break and create a new block
        break;
      }

      // Otherwise, keep appending to the same block
      seg.items.forEach(({ data, index }) =>
        block.rows.push({ type: "item", data, globalIndex: index })
      );
      i++;
    }

    out.push(block);
  }

  return out;
}, [segments, isSplit]);

  const totalColumns = 7 + (isDiscountShow ? 2 : 0) + (isGstShow ? 2 : 0);
  const totalLabelColSpan = totalColumns - 1;
  let globalSr = 0;

  const exportToExcel = () => {
    const container = tablesWrapRef.current;
    if (!container) return;
    const wb = XLSX.utils.book_new();
    const tables = Array.from(container.querySelectorAll("table"));
    tables.forEach((tbl, idx) => {
      const clone = tbl.cloneNode(true) as HTMLTableElement;
      clone.querySelectorAll(".no-print").forEach((el) => el.remove());
      clone.querySelectorAll("th.no-print, td.no-print").forEach((cell) => {
        (cell as HTMLElement).innerHTML = "";
      });
      const ws = XLSX.utils.table_to_sheet(clone, { raw: true });
      const headerCells = Array.from(
        clone.querySelectorAll("tr:first-child > th, thead th")
      ) as HTMLElement[];
      if (headerCells.length) {
        const cols = headerCells.map((th) => {
          const w = th.style?.width || "";
          if (w.endsWith("%")) {
            const pct = parseFloat(w) || 0;
            const ch = Math.max(8, Math.round(pct / 3));
            return { wch: ch };
          }
          if (w.endsWith("px")) {
            const px = parseFloat(w) || 0;
            const ch = Math.max(8, Math.round(px / 8));
            return { wch: ch };
          }
          return { wch: 12 };
        });
        (ws as any)["!cols"] = cols;
      }
      XLSX.utils.book_append_sheet(wb, ws, `Table ${idx + 1}`);
    });
    const fname = `Quotation_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fname);
  };

  const renderSingleTable = (table: TableBlock, tableIdx: number) => {
    const tableTotal = table.rows.reduce((sum, r) => {
      if (r.type === "item") return sum + Number(r.data?.attribute?.totalPrice || 0);
      return sum;
    }, 0);

    return (
      <div key={`tbl-${tableIdx}`} style={styles.tableWrap}>
        <table style={styles.table}>
          <tbody>
            <tr style={styles.tableHead}>
              <th style={{ ...styles.tableCell, width: "5%" }}>Sr. No.</th>
              <th
                style={{
                  ...styles.tableCell,
                  whiteSpace: "nowrap",
                  verticalAlign: "middle",
                  textAlign: "left",
                  width:
                    isDiscountShow && isGstShow
                      ? "25%"
                      : isDiscountShow || isGstShow
                      ? "32%"
                      : "49%",
                }}
              >
                Product Code Name
              </th>
              <th style={{ ...styles.tableCell, width: "10%" }}>Company</th>
              <th style={{ ...styles.tableCell, width: "10%" }}>Pack</th>
              <th style={{ ...styles.tableCell, width: "10%" }}>Price (Rs.)</th>
              {isDiscountShow && (
                <>
                  <th style={{ ...styles.tableCell, width: "7%" }}>Disc (%)</th>
                  <th style={{ ...styles.tableCell, width: "10%" }}>Disc Price (Rs.)</th>
                </>
              )}
              {isGstShow && (
                <>
                  <th style={{ ...styles.tableCell, width: "7%" }}>GST (%)</th>
                  <th style={{ ...styles.tableCell, width: "10%" }}>GST Price (Rs.)</th>
                </>
              )}
              <th style={{ ...styles.tableCell, width: "6%" }}>Qty</th>
              <th style={{ ...styles.tableCell, width: "10%" }}>Total</th>
            </tr>

            {table.rows.map((row:any, idx) => {
              if(row.type === "header" && !isSplit){return null}
              if (row.type === "header" ) {
                return (
                  <tr key={`hdr-${tableIdx}-${idx}`}>
                    <td colSpan={totalColumns} style={styles.sectionHeaderCell}>
                      {row.categoryName || ""}
                    </td>
                  </tr>
                );
              }

              const p = row.data;
              const srNo = ++globalSr;
              const gIdx = row.globalIndex;

              const isEditing = (f: any) => !!edit && edit.gIdx === gIdx && edit.field === f;

              const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") commitEdit();
                if (e.key === "Escape") cancelEdit();
              };

              return (
                <tr
                  key={p?.id || `${tableIdx}-${idx}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add("dnd-hover");
                  }}
                  onDragLeave={(e) => e.currentTarget.classList.remove("dnd-hover")}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("dnd-hover");
                    const fromStr = e.dataTransfer.getData("text/plain");
                    const from = dragFromRef.current ?? (fromStr ? parseInt(fromStr, 10) : NaN);
                    dragFromRef.current = null;
                    if (Number.isNaN(from)) return;
                    performMove(from, gIdx);
                  }}
                  className="dnd-row"
                  title="Drag the handle to move this row"
                >
                  <td style={{ ...styles.tableCell2, width: "9%" }}>
                    <div style={styles.srCellInner}>
                      <span>{srNo}</span>
                      <span className="no-print" style={{ display: "inline-flex", gap: 6 }}>
                        <button
                          type="button"
                          style={styles.iconBtn}
                          draggable
                          onDragStart={(e) => onDragStartHandle(gIdx, e)}
                          onDragEnd={() => (dragFromRef.current = null)}
                          title="Drag to move"
                        >
                          ⠿
                        </button>
                        <button
                          type="button"
                          style={styles.iconBtn}
                          onClick={() => moveUp(gIdx)}
                          title="Move up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          style={styles.iconBtn}
                          onClick={() => moveDown(gIdx)}
                          title="Move down"
                        >
                          ▼
                        </button>
                        <Popconfirm
                          title="Are you sure delete this product?"
                          onConfirm={() => handleDelete(gIdx, row)}
                          onCancel={() => {}}
                          okText="Yes"
                          cancelText="No"
                        >
                          <button type="button" style={styles.iconBtn} title="Delete row">
                            ✕
                          </button>
                        </Popconfirm>
                        <button
                          type="button"
                          style={styles.iconBtn}
                          onClick={() => {
                            setAddIndex(gIdx);
                            setIsAddOpen(true);
                          }}
                          title="Add row"
                        >
                          <FaPlus />
                        </button>
                      </span>
                    </div>
                  </td>

                  <td
                    style={{
                      ...styles.tableCell2,
                      ...styles.tableCell3,
                      width:
                        isDiscountShow && isGstShow
                          ? "25%"
                          : isDiscountShow || isGstShow
                          ? "32%"
                          : "49%",
                    }}
                  >
                    {p?.name && p?.attribute?.companyProductCode && p?.name !== p?.attribute?.companyProductCode
                      ? removeWord(p?.name, String(p?.attribute?.companyProductCode))
                      : p?.name}
                    {p?.attribute?.companyProductCode &&
                      p?.name !== p?.attribute?.companyProductCode &&
                      ` - (${p?.attribute?.companyProductCode})`}
                    <br />
                    {p?.attribute?.description}
                  </td>

                  <td
                    style={{ ...styles.tableCell2, width: "10%" }}
                    onClick={() => {
                      setEditIndex(gIdx);
                      setIsEditOpen(true);
                    }}
                  >
                    {p?.attribute?.companyName}
                  </td>

                  <td
                    style={{ ...styles.tableCell2, width: "10%" }}
                    onClick={() => {
                      setEditIndex(gIdx);
                      setIsEditOpen(true);
                    }}
                  >
                    {p?.attribute?.qty} {p?.attribute?.unitName}
                  </td>

                  {/* PRICE (editable) */}
                  <td
                    style={isEditing("price") ? styles.tableCellEditable : styles.tableCell2}
                    onClick={() => beginEdit(gIdx, "price", p?.attribute?.price)}
                  >
                    {isEditing("price") ? (
                      <input
                        autoFocus
                        type="number"
                        step="0.01"
                        className="cell-input"
                        style={styles.input}
                        value={edit?.value ?? ""}
                        onChange={(e) => setEdit((s) => (s ? { ...s, value: e.target.value } : s))}
                        onBlur={commitEdit}
                        onKeyDown={onInputKey}
                      />
                    ) : (
                      <>
                        {(isDiscountShow && isGstShow
                          ? p?.attribute?.price
                          : isDiscountShow && !isGstShow
                          ? p?.attribute?.price
                          : !isDiscountShow && isGstShow
                          ? p?.attribute?.discountAmount
                          : p?.attribute?.gstAmount) > 0
                          ? isDiscountShow && isGstShow
                            ? p?.attribute?.price
                            : isDiscountShow && !isGstShow
                            ? p?.attribute?.price
                            : !isDiscountShow && isGstShow
                            ? p?.attribute?.discountAmount
                            : p?.attribute?.gstAmount
                          : "-"}
                      </>
                    )}
                  </td>

                  {isDiscountShow && (
                    <>
                      {/* DISCOUNT % (editable) */}
                      <td
                        style={isEditing("discount") ? styles.tableCellEditable : styles.tableCell2}
                        onClick={() => beginEdit(gIdx, "discount", p?.attribute?.discount)}
                      >
                        {isEditing("discount") ? (
                          <input
                            autoFocus
                            type="number"
                            step="0.01"
                            className="cell-input"
                            style={styles.input}
                            value={edit?.value ?? ""}
                            onChange={(e) => setEdit((s) => (s ? { ...s, value: e.target.value } : s))}
                            onBlur={commitEdit}
                            onKeyDown={onInputKey}
                          />
                        ) : (
                          <>{p?.attribute?.discount}%</>
                        )}
                      </td>

                      <td style={{ ...styles.tableCell2, width: "10%" }}>
                        {p?.attribute?.discountAmount > 0 ? p?.attribute?.discountAmount : "-"}
                      </td>
                    </>
                  )}

                  {isGstShow && (
                    <>
                      {/* GST % (editable) */}
                      <td
                        style={
                          isEditing("gst_percentage") ? styles.tableCellEditable : styles.tableCell2
                        }
                        onClick={() => beginEdit(gIdx, "gst_percentage", p?.attribute?.gst_percentage)}
                      >
                        {isEditing("gst_percentage") ? (
                          <input
                            autoFocus
                            type="number"
                            step="0.01"
                            className="cell-input"
                            style={styles.input}
                            value={edit?.value ?? ""}
                            onChange={(e) => setEdit((s) => (s ? { ...s, value: e.target.value } : s))}
                            onBlur={commitEdit}
                            onKeyDown={onInputKey}
                          />
                        ) : (
                          <>{p?.attribute?.gst_percentage}%</>
                        )}
                      </td>

                      <td style={{ ...styles.tableCell2, width: "10%" }}>
                        {p?.attribute?.gstAmount > 0 ? p?.attribute?.gstAmount : "-"}
                      </td>
                    </>
                  )}

                  {/* QTY (quotationQty) editable */}
                  <td
                    style={isEditing("quotationQty") ? styles.tableCellEditable : styles.tableCell2}
                    onClick={() => beginEdit(gIdx, "quotationQty", p?.attribute?.quotationQty)}
                  >
                    {isEditing("quotationQty") ? (
                      <input
                        autoFocus
                        type="number"
                        step="1"
                        className="cell-input"
                        style={styles.input}
                        value={edit?.value ?? ""}
                        onChange={(e) => setEdit((s) => (s ? { ...s, value: e.target.value } : s))}
                        onBlur={commitEdit}
                        onKeyDown={onInputKey}
                      />
                    ) : (
                      <>{p?.attribute?.quotationQty}</>
                    )}
                  </td>

                  <td style={{ ...styles.tableCell2, width: "10%" }}>
                    {p?.attribute?.totalPrice > 0 ? p?.attribute?.totalPrice : "-"}
                  </td>
                </tr>
              );
            })}

            <tr>
              <td colSpan={totalLabelColSpan} style={styles.totalCell}>
                Total Amount
              </td>
              <td style={styles.totalAmount}>{tableTotal.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        <style>{`.dnd-row.dnd-hover { outline: 2px dashed ${borderColor}; outline-offset: -2px; }`}</style>

        <QuotationProductEditModal
          open={isEditOpen}
          row={editIndex != null ? items[editIndex] : null}
          onCancel={() => {
            setIsEditOpen(false);
            setEditIndex(null);
          }}
          onSave={async (vals) => {
            if (editIndex == null) return;
            const row = items[editIndex];
            if (!row) return;

            const payload = {
              qty: Number(vals.qtyPack || 0),
              price: vals.price,
              discount: vals.discount,
              discountValue: vals.discountValue,
              discountAmount: vals.discountAmount,
              gstAmount: vals.gstAmount,
              totalPrice: vals.totalPrice,
              quotationQty: vals.quotationQty,
              gst_percentage: vals.gst_percentage,
              gstValue: vals.gstValue,
              updatedDttm: Date.now(),
              id: row?.attribute?.id,
              quotationId: row?.attribute?.quotationId,
              companyId: vals.companyId,
              productId: vals.productId,
              unitId: vals.unitId,
            };
            try {
              await update_quotation_single_product(payload);
              const next = items.map((r, i) =>
                i === editIndex
                  ? {
                      ...r,
                      attribute: {
                        ...r.attribute,
                        companyId: vals.companyId,
                        companyName: vals.companyName,
                        unitName: vals.unitName,
                        qty: Number(vals.qtyPack || 0),
                        price: vals.price,
                        discount: vals.discount,
                        discountValue: vals.discountValue,
                        discountAmount: vals.discountAmount,
                        gst_percentage: vals.gst_percentage,
                        gstValue: vals.gstValue,
                        gstAmount: vals.gstAmount,
                        quotationQty: vals.quotationQty,
                        totalPrice: vals.totalPrice,
                        productId: vals.productId,
                        unitId: vals.unitId,
                      },
                      name: vals.productName,
                      id: vals.productId,
                    }
                  : r
              );
              setItems(next);
              onChangeRows?.(next);
              setIsEditOpen(false);
              setEditIndex(null);
            } catch (error: any) {
              message.error(error?.message || "Something went wrong!");
            }
          }}
        />

        <QuotationProductAddModal
          open={isAddOpen}
          row={addIndex != null ? items[addIndex] : null}
          onCancel={() => {
            setIsAddOpen(false);
            setAddIndex(null);
          }}
          onSave={async (vals) => {
            if (addIndex == null) return;
            const anchorRow = items[addIndex]; // row where (+) was clicked
            if (!anchorRow) return;

            try {
              // 1) create the new product (IGNORE srno from modal to avoid the 0.01 bug)
              const { srno: _ignoreSrno, ...payload } = vals as any; // <-- key line
              const res: any = await create_quotation_single_product(payload);

              if (res?.data?.insertedProductId > 0) {
                const attributeRowId = res.data.insertedProductId;
                const categoryId = res?.data?.productcategoryId;

                // 2) place the new row AFTER the anchor row in DB sequence
                try {
                  await update_quotation_sequence({
                    from_id: attributeRowId,
                    to_id: anchorRow?.attribute?.id,
                    place: "after",
                  });
                } catch (seqErr: any) {
                  console.error(seqErr);
                  message.error(seqErr?.message || "Failed to position new product, local order shown.");
                }

                // 3) optimistic local insert after anchor + contiguous renumbering
                const newItem = {
                  attribute: {
                    id: attributeRowId,
                    companyId: vals.companyId,
                    companyName: vals.companyName,
                    unitName: vals.unitName,
                    qty: Number(vals.qty || 0),
                    price: vals.price,
                    discount: vals.discount,
                    discountValue: vals.discountValue,
                    discountAmount: vals.discountAmount,
                    gst_percentage: vals.gst_percentage,
                    gstValue: vals.gstValue,
                    gstAmount: vals.gstAmount,
                    quotationQty: vals.quotationQty,
                    totalPrice: vals.totalPrice,
                    departmentId: vals.departmentId,
                    categoryId,
                    quotationId: vals.quotationId,
                    productId: vals.productId,
                    unitId: vals.unitId,
                  },
                  name: vals.productName,
                  id: vals.productId,
                  quotationQty: vals.quotationQty,
                };

                const next = [
                  ...items.slice(0, addIndex + 1),
                  newItem,
                  ...items.slice(addIndex + 1),
                ].map((row, i) => ({
                  ...row,
                  attribute: { ...row.attribute, srno: i + 1 },
                }));

                setItems(next);
                onChangeRows?.(next);
                message.success("Product added in quotation!");

                setIsAddOpen(false);
                setAddIndex(null);
              }
            } catch (error: any) {
              message.error(error?.message || "Something went wrong!");
            }
          }}
        />
      </div>
    );
  };

  return (
    <>
      <PrintHider />

      <div className="no-print" style={styles.exportWrap}>
        <Switch value={isSplit} size="small" onChange={(checked) => setIsSplit(checked)} style={{marginRight:"10px",marginTop:"7px"}} />
        <button type="button" style={styles.exportBtn} onClick={exportToExcel}>
          ⬇️ Download Excel
        </button>
      </div>
      <div className="no-print" style={styles.searchBarWrap}>
         <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products…"
          style={styles.searchInput}
        />
      </div>

      <div ref={tablesWrapRef}>{tables.map(renderSingleTable)}</div>
    </>
  );
};

export default OrderViewOnetable;
